import React, { useEffect } from 'react';
import styled from '@emotion/styled';
import { useDispatch } from 'react-redux';
import { removeNotification } from '../store/slices/uiSlice';

const ToastWrapper = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 10000;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Toast = styled.div`
  min-width: 300px;
  max-width: 500px;
  padding: 0.75rem 1rem;
  border-radius: 24px;
  background-color: ${props => props.isDark ? '#1f2937' : 'white'};
  border: ${props => props.isDark ? '1px solid #374151' : '1px solid #e5e7eb'};
  box-shadow: ${props => props.isDark ? '0 4px 12px rgba(0, 0, 0, 0.3)' : '0 4px 12px rgba(0, 0, 0, 0.1)'};
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-weight: 500;
  animation: ${props => props.isExiting ? 'slideOut 0.3s ease-in forwards' : 'slideIn 0.3s ease-out'};
  cursor: pointer;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${props => props.isDark ? '0 6px 16px rgba(0, 0, 0, 0.4)' : '0 6px 16px rgba(0, 0, 0, 0.15)'};
  }
  
  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
  
  @keyframes slideOut {
    from {
      transform: translateX(0);
      opacity: 1;
    }
    to {
      transform: translateX(100%);
      opacity: 0;
    }
  }
`;

const Icon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  flex-shrink: 0;
  font-size: 1.2rem;
  text-shadow: 0 0 4px rgba(0, 0, 0, 0.3), 0 0 8px rgba(255, 255, 255, 0.5);
  
  ${props => {
    switch (props.type) {
      case 'success':
        return `
          background: #9acd32;
          color: #ffffff;
        `;
      case 'error':
        return `
          background: #ff6b35;
          color: #ffffff;
        `;
      case 'warning':
        return `
          background: #ffa500;
          color: #ffffff;
        `;
      default:
        return `
          background: #007ba7;
          color: #ffffff;
        `;
    }
  }}
`;

const Message = styled.div`
  flex: 1;
  line-height: 1.4;
  color: ${props => props.isDark ? '#f9fafb' : '#374151'};
  font-size: 0.875rem;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.isDark ? '#9ca3af' : '#6b7280'};
  font-size: 1.25rem;
  cursor: pointer;
  padding: 0;
  opacity: 0.7;
  transition: opacity 0.2s;
  flex-shrink: 0;
  
  &:hover {
    opacity: 1;
    color: ${props => props.isDark ? '#f9fafb' : '#374151'};
  }
`;

const ToastComponent = ({ notification, onRemove }) => {
  const dispatch = useDispatch();
  const [isExiting, setIsExiting] = React.useState(false);
  
  // Default to dark mode
  const isDark = true;

  const handleRemove = () => {
    setIsExiting(true);
    // Wait for animation to complete before actually removing
    setTimeout(() => {
      onRemove(notification.id);
    }, 300);
  };

  useEffect(() => {
    if (notification.duration > 0) {
      const timer = setTimeout(() => {
        handleRemove();
      }, notification.duration);

      return () => clearTimeout(timer);
    }
  }, [notification.id, notification.duration]);

  const getDefaultIcon = (type) => {
    switch (type) {
      case 'success': return '🚀';
      case 'error': return '💥';
      case 'warning': return '⛔';
      default: return 'ℹ️';
    }
  };

  const getMessageWithoutEmoji = (message) => {
    // Remove emoji from the beginning of the message
    return message.replace(/^[\u{1F600}-\u{1F64F}]|[\u{1F300}-\u{1F5FF}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]\s*/u, '');
  };

  const messageWithoutEmoji = getMessageWithoutEmoji(notification.message);

  return (
    <Toast
      type={notification.type}
      isDark={isDark}
      isExiting={isExiting}
      onClick={handleRemove}
    >
      <Icon type={notification.type}>
        {getDefaultIcon(notification.type)}
      </Icon>
      <Message isDark={isDark}>
        {messageWithoutEmoji}
      </Message>
      <CloseButton isDark={isDark} onClick={(e) => {
        e.stopPropagation();
        handleRemove();
      }}>
        ×
      </CloseButton>
    </Toast>
  );
};

const ToastContainer = ({ notifications, onRemove }) => {
  if (!notifications || notifications.length === 0) {
    return null;
  }

  return (
    <ToastWrapper>
      {notifications.map(notification => (
        <ToastComponent
          key={notification.id}
          notification={notification}
          onRemove={onRemove}
        />
      ))}
    </ToastWrapper>
  );
};

export default ToastContainer;
